import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit2, Globe, Star, Crown, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
  active: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
  paused: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400',
  completed: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
};

const TYPE_STYLES = {
  client: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  internal: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400',
  outsource: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
  saas: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400',
  ecommerce: 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-400',
  other: 'bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400',
};

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-none text-slate-300 dark:text-slate-600'}`}
        />
      ))}
    </div>
  );
}

function Avatar({ name, avatar, size = 'md' }) {
  const dims = size === 'lg' ? 'h-14 w-14 text-lg' : 'h-11 w-11 text-sm';
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  if (avatar) {
    return <img src={avatar} alt={name} className={`${dims} rounded-full object-cover border-2 border-white dark:border-slate-800 shadow`} />;
  }
  return (
    <div className={`${dims} rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center font-semibold shadow`}>
      {initials}
    </div>
  );
}

/**
 * Team member card. Represents a person who works on the project — the lead
 * (project owner) or a member. Salary/assignment values are intentionally NOT
 * shown here; the hierarchy is about who works on the project, not payouts.
 */
function MemberCard({ member, isOwner = false }) {
  const { t } = useTranslation('projectDetail');
  return (
    <div
      className={`relative flex flex-col items-center gap-2 rounded-xl border p-4 w-44 bg-white dark:bg-slate-800 shadow-sm ${
        isOwner
          ? 'border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-300 dark:ring-emerald-700'
          : 'border-slate-200 dark:border-slate-700'
      }`}
      data-testid={isOwner ? 'hierarchy-owner' : `hierarchy-member-${member.member_id || member.employee_id}`}
    >
      {isOwner && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white whitespace-nowrap">
          <Crown className="h-3 w-3" /> {t('team.leadBadge')}
        </span>
      )}
      <Avatar name={member.full_name} avatar={member.avatar} size={isOwner ? 'lg' : 'md'} />
      <div className="text-center min-w-0 w-full">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
          {member.full_name || t('team.unknown')}
        </div>
        {member.position && (
          <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{member.position}</div>
        )}
        {member.status === 'inactive' && (
          <span className="mt-1.5 inline-block rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
            {t('team.inactive')}
          </span>
        )}
      </div>
    </div>
  );
}

function ProjectDetail() {
  const { t } = useTranslation('projectDetail');
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'employee';

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      // Project meta (from the list) + the roster. The /members endpoint is
      // visible to any project member — not gated by the accountant/employee
      // role restriction the way /assignments is — so the whole team sees it.
      const [projectsRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${projectId}/members`, { withCredentials: true }),
      ]);
      const found = (projectsRes.data || []).find((p) => p.project_id === projectId);
      if (!found) {
        toast.error(t('toast.notFound'));
        navigate('/projects');
        return;
      }
      setProject(found);
      setMembers(membersRes.data?.members || []);
    } catch (err) {
      console.error('Error loading project detail:', err);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const owner = useMemo(() => members.find((m) => m.is_owner) || null, [members]);
  const teamMembers = useMemo(() => members.filter((m) => !m.is_owner), [members]);
  const totalMembers = members.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Back + title */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/projects')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 mb-3"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="h-4 w-4" /> {t('common:nav.projects')}
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              {project.name}
            </h1>
            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[project.status] || STATUS_STYLES.completed}`}>
              {t(`status.${project.status}`, { defaultValue: project.status })}
            </span>
            {project.project_type && (
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[project.project_type] || TYPE_STYLES.other}`}>
                {t(`type.${project.project_type}`, { defaultValue: project.project_type })}
              </span>
            )}
          </div>
        </div>
        {!isReadOnly && (
          <Button variant="outline" onClick={() => navigate('/projects', { state: { editProjectId: project.project_id } })}>
            <Edit2 className="h-4 w-4 mr-2" /> {t('common:actions.edit')}
          </Button>
        )}
      </div>

      {/* Info + meta grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">{t('common:fields.description')}</h2>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
            {project.description || t('info.noDescription')}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-4">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('info.domain')}</div>
            {project.domain ? (
              <a href={project.domain.startsWith('http') ? project.domain : `https://${project.domain}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline break-all">
                <Globe className="h-3.5 w-3.5 shrink-0" /> {project.domain}
              </a>
            ) : <span className="text-sm text-slate-400">—</span>}
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('info.rating')}</div>
            {project.rating > 0 ? <StarRating rating={project.rating} /> : <span className="text-sm text-slate-400">—</span>}
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t('info.merchantName')}</div>
            <div className="text-sm text-slate-700 dark:text-slate-300 break-words">{project.merchant_name || '—'}</div>
          </div>
        </div>
      </div>

      {/* Team hierarchy */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('team.title')}</h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          {t('team.subtitle')} {totalMembers > 0 && <span className="font-medium text-slate-700 dark:text-slate-300">{t('team.memberCount', { count: totalMembers })}</span>}
        </p>

        {/* Tree — pt-5 gives the LEAD badge room so overflow-x-auto doesn't clip it */}
        <div className="overflow-x-auto">
          <div className="flex flex-col items-center min-w-max mx-auto px-6 pt-5 pb-2">
            {owner ? (
              <MemberCard member={owner} isOwner />
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-4 text-sm text-slate-400 w-56 text-center">
                {t('team.noOwner')}
              </div>
            )}

            {teamMembers.length > 0 && (
              <>
                <div className="w-px h-8 bg-slate-300 dark:bg-slate-600" />
                <div className="relative flex gap-6 pt-8">
                  {teamMembers.length > 1 && (
                    <div
                      className="absolute top-0 h-px bg-slate-300 dark:bg-slate-600"
                      style={{ left: '5.5rem', right: '5.5rem' }}
                    />
                  )}
                  {teamMembers.map((m) => (
                    <div key={m.member_id || m.employee_id} className="relative">
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-px h-8 bg-slate-300 dark:bg-slate-600" />
                      <MemberCard member={m} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {teamMembers.length === 0 && !owner && (
            <p className="mt-2 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="no-team-members">
              {t('team.empty')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;
